import React, { useMemo } from 'react';
import { twMerge } from 'utilities/src/twMerge';
import ga4 from 'tracking/src/ga4';
import PurifiedText from '../../PurifiedText';
import addUrlHostName from '../../../utils/addUrlHostname';
import BrandedLeading from '../../BrandedLeading';
import MenuItem, { IconEnum } from '../../../data/MenuItem';
import createTransitionEndHandler from '../../../utils/transitions';

import { flyoutIcons } from '../_icons';

type MegaMenuProps = {
  subMenuItem: MenuItem;
  classes?: string;
};

function MegaMenu({ subMenuItem, classes }: MegaMenuProps) {
  // const mainItem = subMenuItem.children[0];
  const subItems = subMenuItem.children;

  const colSpanClassName = (index: number) =>
    index === 0 ? 'lgS:col-span-3 mdL:col-span-2' : 'lgS:col-span-2 mdL:col-span-1';

  const handleTransitionEnd = useMemo(
    () =>
      createTransitionEndHandler({
        onVisible: () =>
          ga4.assignEvent(process.env.NEXT_PUBLIC_GA4, 'old_topnav_viewed', {
            type: 'navbar_menu_event',
            url: document.URL,
          }),
      }),
    [],
  );

  return (
    <div
      className="lgS:ml-24 mdL:left-0 lgS:left-auto lgS:right-0 lgS:max-w-[80rem] mdL:max-w-[100vw] invisible absolute inset-y-2/4 h-auto w-max transform opacity-0 duration-300 group-hover:visible group-hover:-inset-y-0 group-hover:opacity-100 "
      onTransitionEnd={handleTransitionEnd}
    >
      <div
        className={twMerge(
          'lgS:grid-cols-9 mdL:grid-cols-5 relative grid bg-white px-10 py-6 text-left drop-shadow-lg',
          classes,
        )}
      >
        {subItems.map((item, index) => (
          <div key={item.id} className={colSpanClassName(index)}>
            <div className="flex">
              {index === 0 && <div className="mr-2 w-7">{flyoutIcons.get(IconEnum.BairesDev)}</div>}
              <div className="flex-1">
                <BrandedLeading
                  as={item.path ? 'a' : 'span'}
                  className={twMerge(
                    'site-paragraph-xl text-site-neutrals-700 hover:text-site-neutrals-900 font-bold',
                    item.path ? 'hover:underline' : '',
                  )}
                  hrefLink={`${item.path}?nav=old`}
                >
                  {item.label}
                </BrandedLeading>

                {item.description && (
                  <PurifiedText as="p" className="site-paragraph-m text-site-neutrals-600 w-[90%] [&_a]:underline">
                    {item.description}
                  </PurifiedText>
                )}
              </div>
            </div>

            <ul className="mt-6 flex flex-col gap-1">
              {item.children.map((subItem) =>
                index === 0 ? (
                  <li className="mb-6" key={subItem.id}>
                    <div className="flex">
                      {subItem.icon && flyoutIcons.get(IconEnum[subItem.icon])}
                      <div className="ml-2">
                        <span className="site-paragraph-m text-site-neutrals-700 font-bold">
                          <a
                            className="hover:text-site-neutrals-900 hover:underline"
                            href={`${addUrlHostName(subItem.path)}?nav=old`}
                          >
                            {subItem.label}
                          </a>
                        </span>
                        <p className="site-paragraph-s text-site-neutrals-600">{subItem.description}</p>
                      </div>
                    </div>
                  </li>
                ) : (
                  <li
                    key={subItem.id}
                    className={twMerge(
                      'text-site-neutrals-700 lgS:w-auto mdL:w-[90%] lgS:h-10 mdL:h-12 -mt-2 flex items-center font-normal',
                    )}
                  >
                    <a
                      className="hover:text-site-neutrals-900 hover:underline"
                      href={`${addUrlHostName(subItem.path)}?nav=old`}
                    >
                      {subItem.label}
                    </a>
                  </li>
                ),
              )}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}

export default MegaMenu;
